import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone, MapPin, Instagram } from 'lucide-react';
import ApiService from '../services/api';
import { getImageUrl, getHomeImageUrl } from '../utils/imageUtils';
import SEO from './SEO';

interface GalleryImage {
  id: number;
  imageUrl: string;
  title?: string;
  description?: string;
}

interface Service {
  id: number;
  name: string;
  price: number;
  description: string;
  isPopular: boolean;
}

interface HomeSettings {
  id: number;
  heroBackgroundImage?: string;
  artistPortraitImage?: string;
  showcaseImage1?: string;
  showcaseImage2?: string;
  showcaseImage3?: string;
  contactBackgroundImage?: string;
  heroTitle: string;
  heroSubtitle?: string;
  heroDescription?: string;
  artistTitle: string;
  artistDescription?: string;
  showcaseDescription?: string;
  testimonialsTitle: string;
  testimonialsDescription?: string;
  contactTitle: string;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const Home: React.FC = () => {
  const [galleryData, setGalleryData] = useState<GalleryImage[]>([]);
  const [popularServices, setPopularServices] = useState<Service[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load home settings
      const settingsResponse = await ApiService.getHomeSettings();
      if (settingsResponse && settingsResponse.settings) {
        setHomeSettings(settingsResponse.settings);
      }
      
      // Load gallery images
      const galleryResponse = await ApiService.getGalleryImages();
      if (galleryResponse && galleryResponse.images && Array.isArray(galleryResponse.images)) {
        setGalleryData(galleryResponse.images.slice(0, 6)); // Limit to 6 images
      }
      
      // Load popular services
      const servicesResponse = await ApiService.getPopularServices();
      if (servicesResponse && servicesResponse.services) {
        setPopularServices(servicesResponse.services.slice(0, 4)); // Limit to 4 services
      }
    } catch (error) {
      console.error('Data load error:', error);
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <SEO 
        title="Sheva Studio - Konya'nın En İyi Nail Art Stüdyosu"
        description="Sheva’da güzelliğin en özel dokunuşlarını keşfedin – Nail art’tan makyaja, çok daha fazlası burada!"
        keywords="nail art, manikür, jel oje, protez tırnak, tırnak tasarımı, güzellik salonu, konya nail art, sheva studio"
        canonical="/"
      />
      <div className="text-white min-h-screen" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
      {/* Hero Section */}
      <section className="relative h-screen flex flex-col overflow-hidden">
        {/* Background Image - Top Section (Expanded) */}
        <div className="relative h-2/3">
          {homeSettings?.heroBackgroundImage ? (
            <div className="absolute inset-0">
              <img 
                src={getHomeImageUrl(homeSettings.heroBackgroundImage)} 
                alt="Hero Background" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
                  ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black">
            <div className="absolute inset-0" style={{ backgroundColor: `${homeSettings?.backgroundColor || '#000000'}80` }}></div>
          </div>
        )}
          
          {/* Top Section - Sheva Studio */}
          <div className="absolute inset-0 flex items-center justify-center z-10 px-4 sm:px-6 lg:px-8" style={{ transform: 'translateY(-5%)' }}>
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem] 2xl:text-[12rem] font-light tracking-wider" style={{ color: '#f2eadd', fontFamily: 'FuturaLTPaneuropean', textTransform: 'lowercase', transform: 'translateY(-10%)' }}>
                {homeSettings?.heroTitle === 'SHEVA STUDIO' ? 'sheva' : (homeSettings?.heroTitle || 'sheva')}
              </h1>
              <div className="mt-1 sm:mt-2 md:mt-3 lg:mt-4 xl:mt-5" style={{ transform: 'translateY(-15%)' }}>
                <div className="flex items-center justify-center">
                  <div className="w-48 sm:w-64 md:w-80 lg:w-96 xl:w-[20rem] 2xl:w-[25rem] h-px" style={{ backgroundColor: '#f2eadd' }}></div>
                  <span className="ml-2 sm:ml-3 md:ml-4 lg:ml-5 xl:ml-6 text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl font-light tracking-wider" style={{ color: '#f2eadd', fontFamily: 'FuturaLTPaneuropean', transform: 'translateY(-2px)' }}>
                    STUDIO X By Sevval Kavak
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Hero Subtitle at bottom */}
          <div className="absolute bottom-0 left-0 right-0 z-10 px-4 sm:px-6 lg:px-8" style={{ transform: 'translateY(115%)' }}>
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl mb-2 sm:mb-3 font-light" style={{ color: '#202528', fontFamily: 'NewYork', transform: 'translateY(-5px)' }}>
                {homeSettings?.heroSubtitle || 'Yaratıcı ruh için profesyonel tırnak sanatı'}
              </p>
              <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl mb-3 sm:mb-4 leading-relaxed px-4" style={{ color: '#000000', fontFamily: 'Montserrat-Light' }}>
                {homeSettings?.heroDescription || 'Benzersiz tarzınızı yansıtan etkileyici tırnak tasarımları oluşturmada uzmanız. Uzman ekibimiz yaratıcılığınızı ifade etmenize yardımcı olmak için kişiselleştirilmiş hizmetler sunar.'}
              </p>

            </div>
          </div>
          

        </div>
      </section>



      {/* Meet the Artist Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Color */}
        <div className="absolute inset-0" style={{ backgroundColor: '#9e906d' }}>
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Vertical SHEVA Text - Left Side */}
          <div className="absolute -left-20 sm:-left-32 md:-left-40 top-[123%] transform -translate-y-1/2">
                          <div className="text-4xl sm:text-6xl md:text-8xl lg:text-12xl xl:text-[16rem] 2xl:text-[20rem] font-light tracking-tight transform -rotate-90 origin-left ml-0 lg:ml-[-65px]" style={{ fontFamily: 'Montserrat-Regular', color: '#f2eadd' }}>
                SHEVA
            </div>
          </div>
          
          {/* Showcase Images */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-0 items-center lg:-space-x-10">
            {/* Left Column - Small Image (Lower Position) */}
            <div className="lg:col-span-3 flex justify-center order-1 lg:order-1">
              <div className="w-8/9 lg:w-8/9 mt-8 lg:mt-16 relative">
                {/* Background Frame - Left Top */}
                <div className="absolute -top-6 -left-6 w-[90%] h-[90%] z-0" style={{ backgroundColor: '#683c31' }}></div>
                
                {homeSettings?.showcaseImage1 ? (
                  <div className="aspect-[4/5] bg-gray-800 relative overflow-hidden group cursor-pointer z-10">
                    <img
                      src={getHomeImageUrl(homeSettings.showcaseImage1)}
                      alt="Showcase 1"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all duration-300"></div>
                  </div>
                ) : galleryData[0] && (
                  <div className="aspect-[4/5] bg-gray-800 relative overflow-hidden group cursor-pointer z-10">
                    <img
                      src={getImageUrl(galleryData[0].imageUrl)}
                      alt={galleryData[0].title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all duration-300"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Center Column - Large Image (Smaller but still larger than others) */}
            <div className="lg:col-span-5 flex justify-center order-2 lg:order-2">
              <div className="w-5/6 lg:w-5/6 relative">
                {/* Rotating Circle Text */}
                <div className="absolute -top-12 -left-12 w-32 h-32" style={{ zIndex: 999 }}>
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <defs>
                      <path id="circle" d="M 50,50 m -40,0 a 40,40 0 1,1 80,0 a 40,40 0 1,1 -80,0" />
                    </defs>
                    <text className="text-xs font-light tracking-wider" style={{ fontFamily: 'FuturaLTPaneuropean', fill: '#f2eadd' }}>
                      <textPath href="#circle" startOffset="0%" className="animate-spin" style={{ animationDuration: '20s' }}>
                        sheva studio • sheva studio • sheva studio • sheva studio • sheva studio • sheva studio • sheva studio • sheva studio
                      </textPath>
                    </text>
                  </svg>
                </div>
                {homeSettings?.showcaseImage2 ? (
                  <div className="aspect-[4/5] bg-gray-800 relative overflow-hidden group cursor-pointer z-10">
                    <img
                      src={getHomeImageUrl(homeSettings.showcaseImage2)}
                      alt="Showcase 2"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all duration-300"></div>
                  </div>
                ) : galleryData[1] && (
                  <div className="aspect-[4/5] bg-gray-800 relative overflow-hidden group cursor-pointer z-10">
                    <img
                      src={getImageUrl(galleryData[1].imageUrl)}
                      alt={galleryData[1].title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all duration-300"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Small Image + Text */}
            <div className="lg:col-span-4 space-y-6 sm:space-y-8 order-3 lg:order-3">
              {/* Small Image (Smaller) */}
              <div className="w-full lg:w-full mx-auto lg:mx-0 relative" style={{ transform: 'translateY(-17px)' }}>
                {/* Background Frame - Right Bottom */}
                <div className="absolute -bottom-7 -right-7 w-[90%] h-[90%] z-0" style={{ backgroundColor: '#552511' }}></div>
                
                {homeSettings?.showcaseImage3 ? (
                  <div className="aspect-square bg-gray-800 relative overflow-hidden group cursor-pointer z-10">
                    <img
                      src={getHomeImageUrl(homeSettings.showcaseImage3)}
                      alt="Showcase 3"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all duration-300"></div>
                  </div>
                ) : galleryData[2] && (
                  <div className="aspect-square bg-gray-800 relative overflow-hidden group cursor-pointer z-10">
                    <img
                      src={getImageUrl(galleryData[2].imageUrl)}
                      alt={galleryData[2].title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBQbGFjZWhvbGRlcjwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all duration-300"></div>
                  </div>
                )}
              </div>

              {/* Text Content */}
              <div className="space-y-4 sm:space-y-6 text-center lg:pl-8">
                <p className="text-sm sm:text-base md:text-lg px-4 lg:px-0" style={{ fontFamily: 'Montserrat-Light', lineHeight: '1.2', width: '100%', maxWidth: 'none', color: '#f2eadd' }}>
                  {homeSettings?.showcaseDescription || 'Benzersiz tarzınızı yansıtan etkileyici tırnak tasarımları oluşturmada uzmanız. Uzman ekibimiz yaratıcılığınızı ifade etmenize yardımcı olmak için kişiselleştirilmiş hizmetler sunar.'}
                </p>
                <div className="flex justify-center">
                  <Link 
                    to="/gallery"
                    className="inline-flex items-center justify-center px-6 py-3 border transition-all duration-300 text-sm tracking-wide"
                    style={{ borderColor: '#f2eadd', color: '#f2eadd' }}
                  >
                    Galerimiz
                    <ArrowRight className="ml-2 w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Client Testimonials Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">

          
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light mb-4 sm:mb-6 md:mb-8 tracking-wider" style={{ fontFamily: 'FuturaLTPaneuropean', color: '#9e906d' }}>
              {homeSettings?.testimonialsTitle || 'MÜŞTERİLERİMİZDEN TATLI SÖZLER'}
            </h2>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl mb-6 sm:mb-8 md:mb-12 leading-relaxed px-4" style={{ color: '#000000', fontFamily: 'Montserrat-Light' }}>
              {homeSettings?.testimonialsDescription || 'Sadece bizim sözümüze güvenmeyin. İşte müşterilerimizin deneyimleri hakkında söyledikleri. Değerli müşterilerimizin güvenini ve memnuniyetini kazanmış olmaktan gurur duyuyoruz.'}
            </p>




        </div>
      </section>

      {/* Contact Section with Footer */}
      <section className="relative">
        {/* Background Image - Covers both Contact and Footer */}
        {homeSettings?.contactBackgroundImage ? (
          <div className="absolute inset-0">
            <img 
              src={getHomeImageUrl(homeSettings.contactBackgroundImage)} 
              alt="Contact Background" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gray-900 opacity-50"></div>
          </div>
        ) : (
          <div className="absolute inset-0 opacity-50" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}></div>
        )}
        
        {/* Contact Section Content */}
        <div className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto" style={{ transform: 'translateY(100px)' }}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16 tracking-wider" style={{ fontFamily: 'Montserrat-Regular', transform: 'translateY(-10px)' }}>
              {homeSettings?.contactTitle || 'BİZE ULAŞIN'}
            </h2>
            
            <div className="text-center" style={{ transform: 'translateY(-20px)' }}>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-300 mb-6 sm:mb-8 md:mb-12 max-w-2xl mx-auto leading-relaxed px-4" style={{ fontFamily: 'Montserrat-Light', transform: 'translateY(-10px)' }}>
              Randevu oluşturmak ya da hizmetlerimiz hakkında detaylı bilgi almak için bizimle iletişime geçin.
              </p>
              
              <Link 
                to="/contact"
                className="inline-flex items-center px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 text-sm sm:text-base md:text-lg lg:text-xl tracking-wide"
                style={{ fontFamily: 'FuturaLTPaneuropean', transform: 'translateY(-20px)' }}
                onClick={() => {
                  // Sayfa yüklendiğinde en üste scroll yap
                  setTimeout(() => {
                    window.scrollTo(0, 0);
                  }, 100);
                }}
              >
                RANDEVU AL
                <ArrowRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="relative z-10 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center space-x-6 sm:space-x-8 mb-6 sm:mb-8">
            <a href="https://www.instagram.com/shevastudioo?igsh=MXVkZTE4Zm52YmNscQ==" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
              <Instagram className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
            <a href="tel:05549894663" className="text-gray-400 hover:text-white transition-colors">
              <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
            <a href="https://share.google/5WzVHJNgqV2PFi4X6" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
          </div>
          <p className="text-gray-500 text-xs sm:text-sm">
            © 2025 Sheva Studio. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>
      </section>
      </div>
    </>
  );
};

export default Home;